import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ClipboardList, Users, Truck, Zap, AlertTriangle, Clock,
  MapPin, Loader, ExternalLink, ArrowRight, RefreshCw
} from 'lucide-react';
import EditJobOrderModal from './EditJobOrderModal';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg: 'var(--nd-bg)', surface: 'var(--nd-surface)', surf2: 'var(--nd-surface-2)', surf3: 'var(--nd-surface-2)',
  border: 'var(--nd-w08)', bord2: 'var(--nd-w10)',
  text: 'var(--nd-text)', muted: 'var(--nd-tx35)',
  blue: '#3B82F6', teal: '#22D3A5', amber: '#FBBF24', red: '#F87171',
  green: '#34D399', purple: '#A78BFA', cyan: '#22D3EE',
  font: "'DM Sans',sans-serif", mono: "'DM Mono',monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin   { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  /* stat cards */
  .od-stat { background:var(--nd-surface); border:1px solid var(--nd-w08); border-radius:14px; padding:18px 20px; display:flex; align-items:center; justify-content:space-between; gap:12px; transition:border-color 0.2s; }
  .od-stat:hover { border-color:var(--nd-w12); }

  /* section cards */
  .od-card { background:var(--nd-surface); border:1px solid var(--nd-w08); border-radius:14px; overflow:hidden; }

  /* table */
  .od-th { padding:10px 14px; font-size:9px; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; color:var(--nd-tx30); font-family:'DM Sans',sans-serif; border-bottom:1px solid var(--nd-w08); white-space:nowrap; background:var(--nd-surface-2); }
  .od-tr { border-bottom:1px solid var(--nd-w05); transition:background 0.12s; }
  .od-tr:last-child { border-bottom:none; }
  .od-tr:hover td { background:var(--nd-w03); }
  .od-td { padding:10px 14px; vertical-align:middle; font-size:12px; }

  /* progress bar */
  .od-bar-track { height:6px; border-radius:99px; background:var(--nd-w08); overflow:hidden; }
  .od-bar-fill  { height:100%; border-radius:99px; transition:width 0.6s ease; }

  /* quick action links */
  .od-qa { display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px; border:1px solid var(--nd-w08); background:var(--nd-w03); color:var(--nd-text); text-decoration:none; font-size:12px; font-weight:600; font-family:'DM Sans',sans-serif; transition:all 0.15s; }
  .od-qa:hover { background:var(--nd-w08); border-color:var(--nd-w15); color:var(--nd-text-strong); }

  /* dispatch button */
  .od-btn-dispatch { display:inline-flex; align-items:center; gap:6px; padding:5px 12px; border-radius:7px; border:none; background:linear-gradient(135deg,#3B82F6,#22D3A5); color:var(--nd-text-strong); font-family:'DM Sans',sans-serif; font-size:11px; font-weight:700; cursor:pointer; transition:opacity 0.15s; }
  .od-btn-dispatch:hover { opacity:.88; }
  .od-btn-reschedule { display:inline-flex; align-items:center; gap:6px; padding:5px 12px; border-radius:7px; border:1px solid rgba(248,113,113,0.28); background:rgba(248,113,113,0.08); color:#F87171; font-family:'DM Sans',sans-serif; font-size:11px; font-weight:700; cursor:pointer; text-decoration:none; transition:all 0.15s; }
  .od-btn-reschedule:hover { background:rgba(248,113,113,0.16); border-color:rgba(248,113,113,0.45); color:#F87171; }

  ::-webkit-scrollbar{width:4px;height:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:var(--nd-w10);border-radius:4px}
`;

// ── Stat card ─────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, iconColor, accent, trend, delay = 0 }) => (
  <div className="od-stat" style={{ animation:`fadeUp 0.3s ease ${delay}s both` }}>
    <div>
      <div style={{ fontSize:10,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',color:T.muted,marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:30,fontWeight:900,color:'var(--nd-text-strong)',fontFamily:T.mono,lineHeight:1 }}>{value}</div>
      {trend && <div style={{ fontSize:11,color:iconColor,fontWeight:600,marginTop:6 }}>{trend}</div>}
    </div>
    <div style={{ width:46,height:46,borderRadius:12,background:`${iconColor}14`,border:`1px solid ${iconColor}28`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
      <Icon size={20} color={iconColor} />
    </div>
  </div>
);

// ── Progress bar ─────────────────────────────────────────────────
const capacityColor = cap => cap < 40 ? T.red : cap < 70 ? T.amber : T.green;

// ══════════════════════════════════════════════════════════════════
const OperationsDashboard = () => {
  const [loading, setLoading]           = useState(false);
  const [stats, setStats]               = useState({ pendingJobs:0, activeTeams:0, availableVehicles:0, totalKwCleaned:0 });
  const [urgentJobs, setUrgentJobs]     = useState([]);
  const [todaysSchedule, setTodaysSchedule] = useState([]);
  const [cityCapacity, setCityCapacity] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedJob, setSelectedJob]   = useState(null);

  const handleDispatchClick = (job) => { setSelectedJob(job); setShowEditModal(true); };

  const getPrevMonthName = () => {
    const d = new Date(); d.setMonth(d.getMonth()-1);
    return d.toLocaleString('default', { month:'long' });
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const formatDate = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

      const todayObj        = new Date();
      const todayStr        = formatDate(todayObj);
      const startOfPrevMonth = new Date(todayObj.getFullYear(), todayObj.getMonth()-1, 1);
      const endOfPrevMonth   = new Date(todayObj.getFullYear(), todayObj.getMonth(), 0);

      const [statsRes, todaysRes, overdueRes, teamsRes, vehiclesRes, machineryRes] = await Promise.all([
        axios.get(`${baseUrl}/job-orders/ops-job-orders`, { params:{ limit:1, status:'Pending' } }),
        axios.get(`${baseUrl}/job-orders/ops-job-orders`, { params:{ specificDate:todayStr, limit:100 } }),
        axios.get(`${baseUrl}/job-orders/ops-job-orders`, { params:{ startDate:formatDate(startOfPrevMonth), endDate:formatDate(endOfPrevMonth), status:'Pending', limit:100 } }),
        axios.get(`${baseUrl}/team-members`),
        axios.get(`${baseUrl}/vehicles`),
        axios.get(`${baseUrl}/machinery`),
      ]);

      const teams     = teamsRes.data    || [];
      const vehicles  = vehiclesRes.data || [];
      const machinery = machineryRes.data|| [];
      const totalKw   = machinery.reduce((s,m)=>s+(Number(m.kw_cleaned)||0),0);

      setStats({ pendingJobs: statsRes.data.total||0, activeTeams: teams.length, availableVehicles: vehicles.length, totalKwCleaned: totalKw });

      const todaysData    = todaysRes.data.rows || [];
      const pendingToday  = todaysData.filter(j => j.status?.toLowerCase()!=='dispatched' && j.status?.toLowerCase()!=='completed');
      console.log(`Today's Jobs (${todayStr}):`, pendingToday.length);
      setTodaysSchedule(pendingToday);

      const overdueData = overdueRes.data.rows || [];
      console.log('Overdue Jobs (Prev Month):', overdueData.length);
      setUrgentJobs(overdueData);

      const cities = ['Karachi','Lahore','Islamabad','Rawalpindi'];
      const capData = cities.map(city => {
        const cityJobs  = todaysData.filter(j=>j.client_city===city && j.status!=='Completed').length;
        const cityTeams = teams.filter(t=>t.city===city).length;
        let cap = 100;
        if (cityTeams===0 && cityJobs>0) cap=0;
        else if (cityTeams>0) cap = Math.max(0, 100-(cityJobs/cityTeams*20));
        return { city, jobs:cityJobs, teams:cityTeams, capacity:Math.round(cap) };
      }).filter(c=>c.jobs>0||c.teams>0);
      setCityCapacity(capData);

    } catch (error) { console.error('Dashboard Error:', error); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDashboardData(); }, []);

  return (
    <div style={{ background:T.bg, fontFamily:T.font, color:T.text, padding:'14px 20px' }}>
      <style>{STYLES}</style>

      {/* Page header */}
      <div style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:16,marginBottom:18,overflow:'hidden' }}>
        <div style={{ padding:'18px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12 }}>
          <div>
            <div style={{ fontSize:9,color:T.muted,letterSpacing:'0.18em',marginBottom:5 }}>iNOVAA · OPERATIONS</div>
            <div style={{ fontSize:26,fontWeight:800,color:'var(--nd-text-strong)',letterSpacing:'-0.03em',lineHeight:1 }}>Operations Command Center</div>
            <div style={{ fontSize:12,color:T.muted,marginTop:5 }}>Live Overview &amp; Dispatch</div>
          </div>
          <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4 }}>
            <div style={{ fontSize:13,color:T.text,fontFamily:T.mono }}>{new Date().toLocaleDateString('en-GB')}</div>
            <div style={{ fontSize:10,color:T.muted }}>Today's Status</div>
            <button onClick={fetchDashboardData} style={{ marginTop:4,display:'flex',alignItems:'center',gap:5,padding:'5px 10px',borderRadius:7,border:`1px solid ${T.border}`,background:'var(--nd-w04)',color:T.muted,fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:T.font,transition:'all 0.15s' }}>
              <RefreshCw size={10} /> Refresh
            </button>
          </div>
        </div>
        <div style={{ height:2,background:'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {loading ? (
        <div style={{ display:'flex',alignItems:'center',justifyContent:'center',padding:'80px',gap:12 }}>
          <Loader size={22} color={T.blue} style={{ animation:'spin 0.8s linear infinite' }} />
          <span style={{ color:T.muted,fontSize:13 }}>Loading dashboard…</span>
        </div>
      ) : (
        <>
          {/* Stats row */}
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))',gap:12,marginBottom:18 }}>
            <StatCard title="Pending Jobs"      value={stats.pendingJobs}                           icon={Clock}         iconColor={T.amber}  trend="Total Backlog"  delay={0}    />
            <StatCard title="Active Teams"      value={stats.activeTeams}                           icon={Users}         iconColor={T.blue}   trend="Field Force"   delay={0.05} />
            <StatCard title="Total Assets"      value={stats.availableVehicles}                     icon={Truck}         iconColor={T.cyan}   trend="Fleet Size"    delay={0.10} />
            <StatCard title="Total kW Cleaned"  value={stats.totalKwCleaned.toLocaleString()}       icon={Zap}           iconColor={T.green}  trend="Performance"   delay={0.15} />
          </div>

          <div style={{ display:'grid',gridTemplateColumns:'1fr minmax(0,380px)',gap:16,alignItems:'start' }}>

            {/* Left column */}
            <div style={{ minWidth:0 }}>

              {/* Overdue jobs */}
              <div className="od-card" style={{ marginBottom:16,animation:'fadeUp 0.3s ease 0.2s both' }}>
                <div style={{ padding:'12px 16px',borderBottom:`1px solid rgba(248,113,113,0.18)`,background:'rgba(248,113,113,0.06)',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                  <div style={{ display:'flex',alignItems:'center',gap:9 }}>
                    <div style={{ width:26,height:26,borderRadius:7,background:'rgba(248,113,113,0.14)',border:'1px solid rgba(248,113,113,0.28)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                      <AlertTriangle size={13} color={T.red} />
                    </div>
                    <span style={{ fontSize:13,fontWeight:700,color:T.text }}>Overdue Jobs <span style={{ color:T.muted,fontWeight:500 }}>({getPrevMonthName()})</span></span>
                  </div>
                  <span style={{ fontSize:10,fontWeight:700,color:T.red,fontFamily:T.mono,background:'rgba(248,113,113,0.10)',border:'1px solid rgba(248,113,113,0.22)',padding:'3px 9px',borderRadius:5 }}>
                    {urgentJobs.length} Pending
                  </span>
                </div>
                <div style={{ maxHeight:280,overflowY:'auto' }}>
                  <table style={{ width:'100%',borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                        {['Job ID','Client','Scheduled','Status','Action'].map((h,i)=>(
                          <th key={h} className="od-th" style={i===4?{textAlign:'right'}:{}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {urgentJobs.length > 0 ? urgentJobs.map(job=>(
                        <tr key={job.job_id} className="od-tr">
                          <td className="od-td"><span style={{ fontFamily:T.mono,fontWeight:700,color:T.red }}>#{job.job_id}</span></td>
                          <td className="od-td" style={{ color:T.text,fontWeight:600 }}>{job.customer_name||'Unknown Client'}</td>
                          <td className="od-td">
                            <span style={{ display:'inline-block',padding:'2px 8px',borderRadius:5,fontSize:10,fontWeight:700,fontFamily:T.mono,background:'rgba(248,113,113,0.12)',color:T.red,border:'1px solid rgba(248,113,113,0.25)' }}>
                              {new Date(job.scheduled_date).toLocaleDateString('en-GB')}
                            </span>
                          </td>
                          <td className="od-td" style={{ color:T.muted }}>{job.status}</td>
                          <td className="od-td" style={{ textAlign:'right' }}>
                            <Link to={`/operations/list-job-orders?search=${job.job_id}`} className="od-btn-reschedule">
                              Reschedule
                            </Link>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={5} style={{ padding:'28px',textAlign:'center',color:T.muted,fontSize:12 }}>
                          No overdue jobs from {getPrevMonthName()}.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Today's dispatch queue */}
              <div className="od-card" style={{ animation:'fadeUp 0.3s ease 0.25s both' }}>
                <div style={{ padding:'12px 16px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                  <div style={{ display:'flex',alignItems:'center',gap:9 }}>
                    <div style={{ width:26,height:26,borderRadius:7,background:'rgba(59,130,246,0.12)',border:'1px solid rgba(59,130,246,0.25)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                      <ClipboardList size={13} color={T.blue} />
                    </div>
                    <span style={{ fontSize:13,fontWeight:700,color:T.text }}>Today's Dispatch Queue</span>
                  </div>
                  <Link to="/operations/list-job-orders" style={{ display:'inline-flex',alignItems:'center',gap:5,padding:'5px 11px',borderRadius:7,border:`1px solid ${T.border}`,background:'var(--nd-w04)',color:T.muted,fontSize:11,fontWeight:700,textDecoration:'none',transition:'all 0.15s' }}>
                    View All <ArrowRight size={10} />
                  </Link>
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%',borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                        {['Client','Location','Time','Status','Action'].map((h,i)=>(
                          <th key={h} className="od-th" style={i===4?{textAlign:'right'}:{}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {todaysSchedule.length > 0 ? todaysSchedule.map(job=>(
                        <tr key={job.job_id} className="od-tr">
                          <td className="od-td" style={{ fontWeight:600,color:T.text }}>{job.customer_name}</td>
                          <td className="od-td"><div style={{ display:'flex',alignItems:'center',gap:5,color:T.muted }}><MapPin size={10} />{job.city||'N/A'}</div></td>
                          <td className="od-td" style={{ fontFamily:T.mono,color:T.muted,fontSize:11 }}>
                            {new Date(job.scheduled_date).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}
                          </td>
                          <td className="od-td">
                            <span style={{ display:'inline-block',padding:'2px 8px',borderRadius:5,fontSize:10,fontWeight:700,fontFamily:T.mono,background:'rgba(251,191,36,0.12)',color:T.amber,border:'1px solid rgba(251,191,36,0.25)' }}>
                              {job.status}
                            </span>
                          </td>
                          <td className="od-td" style={{ textAlign:'right' }}>
                            <button className="od-btn-dispatch" onClick={() => handleDispatchClick(job)}>
                              Dispatch
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={5} style={{ padding:'28px',textAlign:'center',color:T.muted,fontSize:12 }}>
                          No pending dispatches for today.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div>
              {/* Regional load */}
              <div className="od-card" style={{ marginBottom:16,animation:'fadeUp 0.3s ease 0.3s both' }}>
                <div style={{ padding:'12px 16px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:9 }}>
                  <div style={{ width:26,height:26,borderRadius:7,background:'rgba(34,211,165,0.12)',border:'1px solid rgba(34,211,165,0.25)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                    <MapPin size={13} color={T.teal} />
                  </div>
                  <div>
                    <div style={{ fontSize:13,fontWeight:700,color:T.text }}>Regional Load</div>
                    <div style={{ fontSize:10,color:T.muted }}>Teams vs Job Demand</div>
                  </div>
                </div>
                <div style={{ padding:'16px' }}>
                  {cityCapacity.length > 0 ? cityCapacity.map((c,i)=>(
                    <div key={i} style={{ marginBottom:i<cityCapacity.length-1?16:0 }}>
                      <div style={{ display:'flex',justifyContent:'space-between',marginBottom:6 }}>
                        <span style={{ fontSize:12,fontWeight:700,color:T.text }}>{c.city}</span>
                        <span style={{ fontSize:11,fontFamily:T.mono,color:capacityColor(c.capacity),fontWeight:700 }}>
                          {c.jobs} Jobs / {c.teams} Teams
                        </span>
                      </div>
                      <div className="od-bar-track">
                        <div className="od-bar-fill" style={{ width:`${c.capacity}%`, background:`linear-gradient(90deg,${capacityColor(c.capacity)}80,${capacityColor(c.capacity)})` }} />
                      </div>
                    </div>
                  )) : (
                    <div style={{ textAlign:'center',color:T.muted,fontSize:12,padding:'16px 0' }}>No regional data available</div>
                  )}
                </div>
              </div>

              {/* Quick actions */}
              <div className="od-card" style={{ animation:'fadeUp 0.3s ease 0.35s both' }}>
                <div style={{ padding:'12px 16px',borderBottom:`1px solid ${T.border}` }}>
                  <span style={{ fontSize:13,fontWeight:700,color:T.text }}>Quick Actions</span>
                </div>
                <div style={{ padding:'12px 14px',display:'flex',flexDirection:'column',gap:8 }}>
                  <Link to="/operations/list-job-orders" className="od-qa">
                    <div style={{ width:28,height:28,borderRadius:8,background:'rgba(59,130,246,0.12)',border:'1px solid rgba(59,130,246,0.22)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                      <ClipboardList size={13} color={T.blue} />
                    </div>
                    Manage Job Orders
                    <ArrowRight size={12} style={{ marginLeft:'auto',opacity:0.4 }} />
                  </Link>
                  <Link to="/operations/team-management" className="od-qa">
                    <div style={{ width:28,height:28,borderRadius:8,background:'rgba(34,211,165,0.12)',border:'1px solid rgba(34,211,165,0.22)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                      <Users size={13} color={T.teal} />
                    </div>
                    Add Team Member
                    <ArrowRight size={12} style={{ marginLeft:'auto',opacity:0.4 }} />
                  </Link>
                  <Link to="/operations/vehicle-machinery-management" className="od-qa">
                    <div style={{ width:28,height:28,borderRadius:8,background:'rgba(34,211,238,0.12)',border:'1px solid rgba(34,211,238,0.22)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                      <Truck size={13} color={T.cyan} />
                    </div>
                    Check Fleet Status
                    <ArrowRight size={12} style={{ marginLeft:'auto',opacity:0.4 }} />
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </>
      )}

      <EditJobOrderModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        jobOrder={selectedJob}
        onJobUpdated={fetchDashboardData}
      />
    </div>
  );
};

export default OperationsDashboard;